import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import { useUploadFileMutation } from "@dashboard/files/api";
import { forbiddenFileTypes, maxFileSize } from "@dashboard/files/hooks/useFileUpload";
import { FileContentType, FileInfo } from "@dashboard/files/types";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useIsWorkspaceFeatureLimitReached,
} from "@dashboard/workspaces/api/helpers";
import { PermissionService } from "@services/permissionService/permissionService";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

type Options = {
    extensions?: string[];
    contentTypes?: FileContentType[];
};

const getUploadContentType = (file: File, contentTypes: FileContentType[] | undefined) => {
    if (!contentTypes?.length || contentTypes.some((item) => item === file.type)) {
        return file.type;
    }
    return contentTypes[0];
};

export const useModuleFileUpload = (options: Options) => {
    const { t } = useTranslation(["common", "pages", "modals"]);
    const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { data: workspace } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();
    const isOverlimit = useIsWorkspaceFeatureLimitReached("FileCountLimit");
    const isExpired = useCurrentSubscriptionExpired();

    const cannotUploadFile = workspaceAbility(workspace).cannot("manage", "File");

    const getDisabledReason = () => {
        if (isExpired) {
            return t("pages:filesPage.tooltips.subscriptionExpired");
        }
        if (cannotUploadFile) {
            return t("modals:manageProductModules.uploadFile.noPermission");
        }
        if (isOverlimit) {
            return t("pages:filesPage.tooltips.overlimit");
        }
        return undefined;
    };

    const upload = async (file: File): Promise<FileInfo | undefined> => {
        const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
        if (options.extensions && !options.extensions.includes(extension)) {
            showErrorToast(
                t("modals:manageProductModules.uploadFile.wrongExtension", {
                    extensions: options.extensions.map((item) => `.${item}`).join(", "),
                }),
            );
            return undefined;
        }
        if (forbiddenFileTypes.includes(extension)) {
            showErrorToast(t("common:section.uploadField.unsupportedFileType"));
            return undefined;
        }
        if (file.size > maxFileSize) {
            showErrorToast(t("common:section.uploadField.maxSizeExceeded"));
            return undefined;
        }
        if (!workspaceId) {
            return undefined;
        }

        const contentType = getUploadContentType(file, options.contentTypes);
        const fileToUpload = contentType === file.type ? file : new File([file], file.name, { type: contentType });
        try {
            const uploadedFile = await uploadFile({
                workspaceId,
                filename: fileToUpload.name,
                fileToUpload,
                controller: new AbortController(),
                groupId: null,
                onUploadProgress: () => {},
                setUploadedFileIds: () => {},
            }).unwrap();
            showSuccessToast(t("common:section.uploadField.toast.fileUploadedSuccess"));
            return uploadedFile;
        } catch {
            // The upload mutation shows its own error toast.
            return undefined;
        }
    };

    return { upload, isUploading, disabledReason: getDisabledReason() };
};
